import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import SearchableSelect from '../components/SearchableSelect';
import DataTable from '../components/DataTable';
import { formatCariOption } from '../utils/cariFormat';
import { fmtAmount } from '../utils/numberFormat';
import { Button, Modal, FormField, ListPageHeader, FilterBar } from '../components/ui';
import { CardHero, CardStatusBadge, CardSplitButton } from '../components/card';
import CustomFieldsSection from '../components/CustomFieldsSection';
import { TAB_ICONS } from '../utils/tabIcons';

const STATUS_LABELS = {
  portfolio: 'Portföyde',
  given_to_bank: 'Bankaya Verilen',
  endorsed: 'Ciro Edilen',
  paid: 'Ödendi',
  dishonored: 'Karşılıksız',
  cancelled: 'İptal',
  company_owned: 'Firma Çeki/Senedi',
};

const STATUS_COLORS = {
  portfolio: '#3b82f6',
  given_to_bank: '#7c3aed',
  endorsed: '#d97706',
  paid: '#16a34a',
  dishonored: '#dc2626',
  cancelled: '#64748b',
  company_owned: '#0284c7',
};

const emptyCheck = {
  doc_type: 'check', direction: 'incoming', portfolio_status: 'portfolio',
  doc_number: '', serial_no: '', account_id: '', issuer_name: '', issuer_tax_no: '',
  bank_name: '', branch_name: '', account_number: '',
  issue_date: new Date().toISOString().slice(0,10),
  due_date: new Date().toISOString().slice(0,10),
  amount: '', currency_code: 'TRY', exchange_rate: 1, notes: '',
};

const cariLabel = (a) => `${a.code || ''} - ${a.title || ''}`;
const cariSub = (a) => {
  const p = [];
  if (a.phone || a.mobile) p.push(a.phone || a.mobile);
  if (a.identity_type === 'individual') { if (a.id_number) p.push('TC: ' + a.id_number); }
  else { if (a.tax_office) p.push(a.tax_office); if (a.tax_number) p.push(a.tax_number); }
  return p.filter(Boolean).join(' | ');
};

function ChecksNotesPage({ docType = 'check', direction = 'incoming', onNavigate = null, autoOpenId = null }) {
  const [items, setItems] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [cashRegisters, setCashRegisters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [showFilterBar, setShowFilterBar] = useState(false);
  const [filters, setFilters] = useState({ date_from: '', date_to: '', status: '', drawer_name: '' });

  const [showModal, setShowModal] = useState(false);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ ...emptyCheck, doc_type: docType, direction });
  const [isNew, setIsNew] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  const [actionMode, setActionMode] = useState(null); // 'give_bank' | 'endorse' | 'mark_paid' | ...
  const [actionForm, setActionForm] = useState({});

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const params = { doc_type: docType, direction };
      if (search) params.search = search;
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const res = await api.get('/checks-notes', { params });
      if (res.data.success) setItems(res.data.data.items);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [docType, direction, search, filters]);

  const fetchLookups = useCallback(async () => {
    try {
      const [aRes, bRes, cRes] = await Promise.all([
        api.get('/accounts', { params: { limit: 500 } }),
        api.get('/bank-accounts'),
        api.get('/cash-registers'),
      ]);
      if (aRes.data.success) setAccounts((aRes.data.data.items || []).filter(a => a.is_active));
      if (bRes.data.success) setBankAccounts((bRes.data.data.items || []).filter(b => b.is_active));
      if (cRes.data.success) setCashRegisters((cRes.data.data.items || cRes.data.data || []).filter(c => c.is_active !== false));
    } catch {}
  }, []);

  useEffect(() => { fetch(); }, [fetch]);
  useEffect(() => { fetchLookups(); }, [fetchLookups]);

  // Deep-link: başka modülden (örn. cari hareketi) belirli bir çek/senet için açıldıysa
  // o kaydı çek ve modal'da göster.
  useEffect(() => {
    if (!autoOpenId) return;
    (async () => {
      try {
        const r = await api.get('/checks-notes/' + autoOpenId);
        if (r.data.success) {
          const rec = r.data.data;
          setSelected(rec);
          setForm({
            ...emptyCheck,
            ...rec,
            issue_date: rec.issue_date ? rec.issue_date.slice(0, 10) : '',
            due_date: rec.due_date ? rec.due_date.slice(0, 10) : '',
          });
          setIsNew(false); setIsEditing(false); setMsg(null);
          setShowModal(true);
        }
      } catch (e) { console.error(e); }
    })();
  }, [autoOpenId]);

  const openNew = () => {
    setSelected(null);
    setForm({ ...emptyCheck, doc_type: docType, direction });
    setIsNew(true); setIsEditing(true); setMsg(null);
    setShowModal(true);
  };

  // Kaynak modülünden gelen otomatik kayıtların referansı için route map
  // (yönlendirme YOK — modal her zaman açılır; sadece kaynak rozeti + link gösterilir)
  const SOURCE_ROUTES = { cari_receipt: 'cari-tahsilat', cari_payment: 'cari-tediye' };
  const SOURCE_NUMBER = (rec) => rec.source_payment_no || rec.source_receipt_no || '';

  const openDetail = (rec) => {
    setSelected(rec);
    setForm({
      ...emptyCheck,
      ...rec,
      issue_date: rec.issue_date ? rec.issue_date.slice(0, 10) : '',
      due_date: rec.due_date ? rec.due_date.slice(0, 10) : '',
    });
    setIsNew(false); setIsEditing(false); setMsg(null);
    setShowModal(true);
  };

  const closeModal = () => { setShowModal(false); setSelected(null); setIsEditing(false); setIsNew(false); setMsg(null); setActionMode(null); };

  const f = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const handleSave = async (mode = 'close') => {
    if (mode && typeof mode === 'object') mode = 'close';
    if (!form.amount || parseFloat(form.amount) <= 0) { setMsg({ type: 'error', text: 'Tutar zorunludur' }); return; }
    if (!form.due_date) { setMsg({ type: 'error', text: 'Vade tarihi zorunludur' }); return; }
    setSaving(true); setMsg(null);
    try {
      if (isNew) {
        const r = await api.post('/checks-notes', form);
        setMsg({ type: 'success', text: 'Kaydedildi' });
        setIsNew(false); setIsEditing(false);
        await fetch();
        if (r.data.data?.id) {
          const det = await api.get('/checks-notes/' + r.data.data.id);
          if (det.data.success) {
            setSelected(det.data.data);
            setForm({ ...emptyCheck, ...det.data.data,
              issue_date: det.data.data.issue_date?.slice(0,10) || '',
              due_date: det.data.data.due_date?.slice(0,10) || '' });
          }
        }
      } else {
        await api.put('/checks-notes/' + selected.id, form);
        setIsEditing(false);
        setMsg({ type: 'success', text: 'Güncellendi' });
        await fetch();
      }
      if (mode === 'close') {
        setTimeout(() => closeModal(), 250);
      } else if (mode === 'new') {
        setTimeout(() => {
          setSelected(null); setForm({ ...emptyCheck });
          setIsEditing(true); setIsNew(true); setMsg(null);
        }, 250);
      } else if (mode === 'copy') {
        setTimeout(() => {
          const copy = { ...form, id: undefined, doc_number: '', serial_no: '' };
          setSelected(null); setForm(copy);
          setIsEditing(true); setIsNew(true); setMsg(null);
        }, 250);
      }
    } catch (err) { setMsg({ type: 'error', text: err.response?.data?.message || 'Hata' }); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!selected || !window.confirm('Silmek istiyor musunuz?')) return;
    try {
      await api.delete('/checks-notes/' + selected.id);
      closeModal(); fetch();
    } catch (err) { alert(err.response?.data?.message || 'Silinemedi'); }
  };

  const performAction = async (action) => {
    try {
      await api.patch('/checks-notes/' + selected.id + '/status', { action, ...actionForm });
      setActionMode(null); setActionForm({});
      const det = await api.get('/checks-notes/' + selected.id);
      if (det.data.success) setSelected(det.data.data);
      fetch();
      setMsg({ type: 'success', text: 'Durum güncellendi' });
    } catch (err) { setMsg({ type: 'error', text: err.response?.data?.message || 'Hata' }); }
  };

  const docLabel = docType === 'check' ? 'Çek' : 'Senet';
  const dirLabel = direction === 'incoming' ? 'Alınan' : 'Verilen';

  // Modal aksiyonları — footer'a taşındı (yeni Modal API).
  // Çek/senet status'üne göre extra "İşlem" aksiyonları body üstündeki kart bloğunda.
  const inEdit = isEditing;
  const inView = !isEditing && !isNew && !!selected;

  const modalTitle = isNew ? `Yeni ${docLabel}` : (form.doc_number || form.serial_no || docLabel);

  return (
    <div className="stocks-page">
      <ListPageHeader
        title={`${dirLabel} ${docLabel}ler`}
        count={items.length}
        search={search}
        onSearch={setSearch}
        searchPlaceholder={`${docLabel} no, seri no, keşideci...`}
        onRefresh={fetch}
        refreshing={loading}
        actions={<Button size="sm" onClick={openNew}>+ Yeni {docLabel}</Button>}
      />

      <FilterBar
        show={showFilterBar}
        onToggle={() => setShowFilterBar(s => !s)}
        values={filters}
        onChange={setFilters}
        fields={[
          { key: 'date_from', type: 'date', label: 'İlk Vade' },
          { key: 'date_to', type: 'date', label: 'Son Vade' },
          { key: 'status', type: 'select', label: 'Durum', options: [
            { value: '', label: 'Tüm Durumlar' },
            ...Object.entries(STATUS_LABELS).map(([k, v]) => ({ value: k, label: v })),
          ] },
          { key: 'drawer_name', type: 'text', label: 'Keşideci', placeholder: 'Keşideci adı...' },
          { key: 'source', type: 'select', label: 'Kaynak', flex: '1 1 200px', minWidth: 180,
            options: [
              { value: '',             label: 'Tümü' },
              { value: 'manual',       label: '✏ Sadece Manuel Girişler' },
              { value: 'cari_receipt', label: '🔗 Tahsilat Makbuzundan' },
              { value: 'auto',         label: '⚡ Tüm Otomatik Üretilen' },
            ] },
        ]}
      />

      <div className="stocks-table-wrap">
        <DataTable
          pageKey={`checks-notes-${docType}-${direction}`}
          tableClassName="stocks-table"
          rowClassName="stocks-table-row"
          loading={loading}
          emptyText="Kayıt yok"
          data={items.filter(r => {
            const src = filters.source;
            if (src === 'manual'       && r.source_type) return false;
            if (src === 'auto'         && !r.source_type) return false;
            if (src === 'cari_receipt' && r.source_type !== 'cari_receipt') return false;
            return true;
          })}
          rowKey="id"
          onRowClick={r => openDetail(r)}
          columns={[
            { id: 'voucher_no', header: 'Belge No', accessor: 'voucher_no', sortable: true, width: 170,
              cell: r => {
                const srcLabels = { cari_receipt: 'Tahsilat', cari_payment: 'Tediye' };
                return (
                  <div style={{ display:'flex', flexDirection:'column', gap: 2 }}>
                    <span style={{ fontFamily: 'monospace', fontWeight: 600, color:'var(--accent-primary)' }}>{r.voucher_no || '—'}</span>
                    {r.source_type && (() => {
                      const srcNo = r.source_payment_no || r.source_receipt_no || '';
                      return (
                        <span title={srcNo ? `Kaynak: ${srcNo}` : ''}
                              style={{ fontSize:'0.66rem', fontWeight: 700, padding:'1px 7px', borderRadius: 100,
                                       background:'rgba(124,58,237,0.10)', color:'#7c3aed',
                                       border:'1px solid rgba(124,58,237,0.25)', alignSelf:'flex-start',
                                       letterSpacing:'0.04em', textTransform:'uppercase' }}>
                          🔗 {srcLabels[r.source_type] || r.source_type}{srcNo ? ' ' + srcNo : ''}
                        </span>
                      );
                    })()}
                  </div>
                );
              } },
            { id: 'doc_no', header: 'Çek/Senet No', accessor: r => r.doc_number || r.serial_no || '', sortable: true, width: 130,
              cell: r => <span style={{ fontFamily: 'monospace', fontSize:'0.82rem' }}>{r.doc_number || r.serial_no || '—'}</span> },
            { id: 'account', header: 'Cari', accessor: r => r.account_title || r.issuer_name || '', sortable: true,
              cell: r => r.account_title || r.issuer_name || '—' },
            { id: 'issuer', header: 'Keşideci', accessor: 'issuer_name', sortable: true,
              cell: r => <span className="stock-secondary">{r.issuer_name || '—'}</span> },
            { id: 'bank', header: 'Banka', accessor: r => r.bank_name ? `${r.bank_name}${r.branch_name ? ' / ' + r.branch_name : ''}` : '', sortable: true,
              cell: r => <span className="stock-secondary">{r.bank_name ? `${r.bank_name}${r.branch_name ? ' / ' + r.branch_name : ''}` : '—'}</span> },
            { id: 'due_date', header: 'Vade', accessor: r => r.due_date || '', sortable: true,
              sortFn: (a, b) => new Date(a.due_date || 0) - new Date(b.due_date || 0), width: 110,
              cell: r => <span className="stock-secondary">{r.due_date ? new Date(r.due_date).toLocaleDateString('tr-TR') : '—'}</span> },
            { id: 'amount', header: 'Tutar', accessor: r => parseFloat(r.amount) || 0, sortable: true,
              headerStyle: { textAlign: 'right' }, cellStyle: { textAlign: 'right' }, width: 160,
              cell: r => <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>{fmtAmount(r.amount || 0)} {r.currency_code}</span> },
            { id: 'status', header: 'Durum', accessor: r => STATUS_LABELS[r.portfolio_status] || r.portfolio_status, sortable: true,
              headerStyle: { textAlign: 'center' }, cellStyle: { textAlign: 'center' }, width: 170,
              cell: r => {
                const color = STATUS_COLORS[r.portfolio_status];
                // Status-specific extra info
                let extra = null;
                if (r.portfolio_status === 'given_to_bank' && r.given_bank_name) {
                  extra = '🏦 ' + r.given_bank_name;
                } else if (r.portfolio_status === 'endorsed' && r.endorsed_to_title) {
                  extra = '➜ ' + r.endorsed_to_title;
                } else if (r.portfolio_status === 'paid') {
                  if (r.paid_to_bank_name) extra = '🏦 ' + r.paid_to_bank_name;
                  else if (r.paid_to_cash_name) extra = '💵 ' + r.paid_to_cash_name;
                }
                return (
                  <div style={{ display:'flex', flexDirection:'column', gap: 2, alignItems:'center' }}>
                    <span style={{ background: color + '22', color, padding: '3px 9px', borderRadius: 4, fontSize: '0.72rem', fontWeight: 600 }}>
                      {STATUS_LABELS[r.portfolio_status] || r.portfolio_status}
                    </span>
                    {extra && <span style={{ fontSize: '0.7rem', color: 'var(--text-tertiary)' }}>{extra}</span>}
                  </div>
                );
              } },
          ]}
        />
      </div>

      {/* DETAY MODAL */}
      {showModal && (
        <Modal
          icon={null}
          title={modalTitle}
          size="lg"
          onClose={closeModal}
          footer={
            <>
              <div className="ui-modal-footer-left">
                {inView && !selected?.source_type
                       && (selected?.portfolio_status === 'portfolio' || selected?.portfolio_status === 'company_owned')
                       && <Button variant="danger" onClick={handleDelete}>🗑 Sil</Button>}
              </div>
              <div className="ui-modal-footer-right" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                {inEdit && (
                  <Button variant="ghost" onClick={() => { if (isNew) closeModal(); else { setIsEditing(false); setForm({ ...emptyCheck, ...selected, issue_date: selected.issue_date?.slice(0,10) || '', due_date: selected.due_date?.slice(0,10) || '' }); } }}>
                    {isNew ? 'Vazgeç' : 'İptal'}
                  </Button>
                )}
                {inEdit && (
                  <CardSplitButton
                    primaryLabel="💾 Kaydet"
                    primaryAction={() => handleSave('close')}
                    loading={saving}
                    options={[
                      { label: 'Kaydet ve Yeni Aç',  icon: '✚', onClick: () => handleSave('new') },
                      { label: 'Kaydet ve Kopyala', icon: '⎘', onClick: () => handleSave('copy') },
                      { label: 'Kaydet ve Kapat',   icon: '✓', onClick: () => handleSave('close') },
                    ]}
                  />
                )}
                {inView && !selected?.source_type
                       && (selected?.portfolio_status === 'portfolio' || selected?.portfolio_status === 'company_owned')
                       && <Button onClick={() => setIsEditing(true)}>✎ Düzenle</Button>}
              </div>
            </>
          }
        >
          {/* Yaşam döngüsü uyarısı — portföyden ayrılmış kayıtlar için */}
          {!isNew && selected && !selected.source_type
             && selected.portfolio_status && selected.portfolio_status !== 'portfolio' && selected.portfolio_status !== 'company_owned' && (
            <div style={{
              margin: '0 0 14px', padding: '10px 14px',
              background: 'rgba(245,158,11,0.10)',
              border: '1px solid rgba(245,158,11,0.30)',
              borderRadius: 8, fontSize: 13,
              display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap'
            }}>
              <span style={{ fontSize: 18 }}>⚠️</span>
              <div style={{ flex: 1, minWidth: 0, color: '#92400e' }}>
                <div style={{ fontWeight: 600 }}>Düzenleme / Silme Kilitli</div>
                <div style={{ fontSize: 12, marginTop: 2 }}>
                  Bu {form.doc_type === 'note' ? 'senet' : 'çek'} <strong>{STATUS_LABELS[selected.portfolio_status] || selected.portfolio_status}</strong> durumunda.
                  Düzenleme/silme yapabilmek için önce mevcut işlemi (
                  {selected.portfolio_status === 'given_to_bank' && 'bankadan geri al'}
                  {selected.portfolio_status === 'endorsed' && 'ciroyu geri al'}
                  {selected.portfolio_status === 'paid' && 'tahsilatı geri al'}
                  {selected.portfolio_status === 'dishonored' && 'karşılıksız işlemini geri al'}
                  {selected.portfolio_status === 'cancelled' && 'iptal işlemini geri al'}
                  ) ile portföye geri alın.
                </div>
              </div>
            </div>
          )}

          {/* Kaynak bilgisi — tahsilat/ödeme makbuzundan oluştuysa banner */}
          {!isNew && selected?.source_type && (
            <div style={{
              margin: '0 0 14px', padding: '10px 14px',
              background: 'rgba(124,58,237,0.08)',
              border: '1px solid rgba(124,58,237,0.25)',
              borderRadius: 8, fontSize: 13,
              display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap'
            }}>
              <span style={{ fontSize: 18 }}>🔗</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, color: '#7c3aed' }}>
                  {selected.source_type === 'cari_receipt' ? 'Tahsilat Makbuzundan' :
                   selected.source_type === 'cari_payment' ? 'Tediye Makbuzundan' : selected.source_type}
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                  Bu {form.doc_type === 'note' ? 'senet' : 'çek'} <strong>{selected.source_payment_no || selected.source_receipt_no || ''}</strong> nolu makbuzdan kesildi.
                  Düzenleme/silme makbuz üzerinden yapılır. Bu ekranda durum işlemleri yapabilirsin.
                  {selected.portfolio_status && selected.portfolio_status !== 'portfolio' && selected.portfolio_status !== 'company_owned' && (
                    <div style={{ marginTop: 4, color: '#92400e', fontWeight: 600 }}>
                      ⚠️ Makbuzu düzenlemek için önce bu kayıttaki <strong>{STATUS_LABELS[selected.portfolio_status]}</strong> işlemini geri almalısın.
                    </div>
                  )}
                </div>
              </div>
              {onNavigate && selected.source_id && SOURCE_ROUTES[selected.source_type] && (
                <Button size="sm" variant="ghost"
                        onClick={() => { setShowModal(false); onNavigate(SOURCE_ROUTES[selected.source_type], selected.source_id); }}>
                  Makbuzu Aç →
                </Button>
              )}
            </div>
          )}

          {/* Hero strip */}
          {!isNew && (
            <CardHero
              icon={form.doc_type === 'note' ? '📜' : '💳'}
              code={form.doc_number || form.serial_no}
              title={form.doc_type === 'note' ? 'Senet' : 'Çek'}
              subtitle={[
                form.bank_name,
                form.branch_name,
                form.account_title,
              ].filter(Boolean).join(' · ')}
              statusBadge={selected?.portfolio_status && STATUS_LABELS[selected.portfolio_status] ? (
                <CardStatusBadge
                  label={
                    STATUS_LABELS[selected.portfolio_status]
                    + (selected.portfolio_status === 'given_to_bank' && selected.given_bank_name ? ` · ${selected.given_bank_name}` : '')
                    + (selected.portfolio_status === 'endorsed' && selected.endorsed_to_title ? ` · ${selected.endorsed_to_title}` : '')
                    + (selected.portfolio_status === 'paid' && selected.paid_to_bank_name ? ` · Banka: ${selected.paid_to_bank_name}` : '')
                    + (selected.portfolio_status === 'paid' && selected.paid_to_cash_name ? ` · Kasa: ${selected.paid_to_cash_name}` : '')
                  }
                  color={STATUS_COLORS[selected.portfolio_status]}
                  bg={STATUS_COLORS[selected.portfolio_status] + '22'}
                />
              ) : null}
              kpis={[
                form.amount && {
                  label: 'Tutar',
                  value: fmtAmount(form.amount),
                  color: form.flow === 'incoming' ? 'success' : 'danger',
                  sublabel: form.currency_code || 'TRY',
                },
                form.due_date && {
                  label: 'Vade',
                  value: new Date(form.due_date).toLocaleDateString('tr-TR'),
                  color: 'warning',
                  sublabel: (() => {
                    const days = Math.ceil((new Date(form.due_date) - new Date()) / 86400000);
                    return days >= 0 ? days + ' gün kaldı' : Math.abs(days) + ' gün geçti';
                  })(),
                },
                form.issue_date && {
                  label: 'Düzenleme', value: new Date(form.issue_date).toLocaleDateString('tr-TR'), color: 'neutral',
                },
              ].filter(Boolean)}
            />
          )}
          {/* Durum etiketi + state-spesifik İşlemler kartı */}
          {!isNew && selected && (
            <div style={{ marginBottom: 12 }}>
              <span style={{ background: STATUS_COLORS[selected.portfolio_status] + '22', color: STATUS_COLORS[selected.portfolio_status], padding: '4px 10px', borderRadius: 4, fontSize: '0.78rem', fontWeight: 600 }}>
                {STATUS_LABELS[selected.portfolio_status]}
              </span>
            </div>
          )}
          {inView && (
            <div style={{ marginBottom: 12, padding: '10px 12px', background: 'var(--bg-secondary)', borderRadius: 8, display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
              <span style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-tertiary)', marginRight: 4 }}>İşlemler:</span>
              {selected.portfolio_status === 'portfolio' && (
                <>
                  <Button variant="ghost" size="sm" onClick={() => { setActionMode('give_bank'); setActionForm({ bank_account_id: '' }); }}>Bankaya Ver</Button>
                  <Button variant="ghost" size="sm" onClick={() => { setActionMode('endorse'); setActionForm({ target_account_id: '' }); }}>Ciro Et</Button>
                </>
              )}
              {(selected.portfolio_status === 'portfolio' || selected.portfolio_status === 'given_to_bank') && (
                <Button variant="ghost" size="sm" onClick={() => {
                  // Her durumda hedef seçtirelim — banka veya kasa
                  setActionMode('mark_paid_bank');
                  setActionForm({ paid_target: selected.portfolio_status === 'given_to_bank' ? 'bank' : 'bank', bank_account_id: selected.given_to_bank_id || '', cash_register_id: '' });
                }}>Ödendi</Button>
              )}
              {selected.portfolio_status !== 'paid' && selected.portfolio_status !== 'cancelled' && selected.portfolio_status !== 'dishonored' && (
                <Button variant="ghost" size="sm" onClick={() => { if (window.confirm('Karşılıksız olarak işaretlensin mi?')) performAction('mark_dishonored'); }}>Karşılıksız</Button>
              )}
              {selected.portfolio_status !== 'portfolio' && (() => {
                const willGoToBank = selected.portfolio_status === 'paid' && selected.given_to_bank_id;
                const label = willGoToBank ? 'Bankaya Geri Al' : 'Portföye Geri Al';
                const confirmMsg = willGoToBank ? 'Ödeme iptal edilip çek bankada bekleyen duruma döndürülsün mü?'
                  : selected.portfolio_status === 'paid' ? 'Ödeme iptal edilip portföye geri alınsın mı?'
                  : selected.portfolio_status === 'dishonored' ? 'Karşılıksız durumu kaldırılıp portföye geri alınsın mı?'
                  : selected.portfolio_status === 'cancelled' ? 'İptal geri alınsın mı?'
                  : 'Portföye iade edilsin mi?';
                return (
                  <Button variant="ghost" size="sm" onClick={() => { if (window.confirm(confirmMsg)) performAction('revert_one_step'); }}>{label}</Button>
                );
              })()}
            </div>
          )}

          {msg && (
            <div style={{
              marginBottom: 12, padding: '9px 12px', borderRadius: 8, fontSize: '0.84rem',
              background: msg.type === 'error' ? 'var(--danger-bg)' : 'rgba(16,185,129,0.1)',
              color: msg.type === 'error' ? 'var(--danger)' : '#0d9488',
              border: '1px solid ' + (msg.type === 'error' ? 'rgba(239,68,68,0.25)' : 'rgba(16,185,129,0.25)'),
            }}>{msg.text}</div>
          )}

          {/* Durum değişikliği inline formları */}
          {actionMode === 'give_bank' && (
            <div style={{ marginBottom: 16, padding: '12px 14px', background: 'var(--bg-secondary)', borderRadius: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>Bankaya Ver:</span>
                <select value={actionForm.bank_account_id || ''} onChange={e => setActionForm({ ...actionForm, bank_account_id: e.target.value })}
                  style={{ padding: '6px 10px', flex: 1 }}>
                  <option value="">Banka hesabı seç</option>
                  {bankAccounts.map(b => <option key={b.id} value={b.id}>{b.account_name} — {b.bank_name}</option>)}
                </select>
                <Button size="sm" onClick={() => performAction('give_to_bank')} disabled={!actionForm.bank_account_id}>Uygula</Button>
                <Button variant="ghost" size="sm" onClick={() => setActionMode(null)}>İptal</Button>
              </div>
            </div>
          )}
          {actionMode === 'mark_paid_bank' && (
            <div style={{ marginBottom: 16, padding: '12px 14px', background: 'var(--bg-secondary)', borderRadius: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>Ödeme Hedefi:</span>
                <label style={{ display:'inline-flex', alignItems:'center', gap: 4, fontSize:'0.85rem' }}>
                  <input type="radio" checked={(actionForm.paid_target || 'bank') === 'bank'}
                         onChange={() => setActionForm({ ...actionForm, paid_target: 'bank', cash_register_id: '' })} />
                  🏦 Banka
                </label>
                <label style={{ display:'inline-flex', alignItems:'center', gap: 4, fontSize:'0.85rem' }}>
                  <input type="radio" checked={actionForm.paid_target === 'cash'}
                         onChange={() => setActionForm({ ...actionForm, paid_target: 'cash', bank_account_id: '' })} />
                  💵 Kasa
                </label>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10, flexWrap:'wrap' }}>
                {(actionForm.paid_target || 'bank') === 'bank' ? (
                  <>
                    <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>Banka Hesabı:</span>
                    <select value={actionForm.bank_account_id || ''} onChange={e => setActionForm({ ...actionForm, bank_account_id: e.target.value })}
                      style={{ padding: '6px 10px', flex: 1, minWidth: 240 }}>
                      <option value="">Banka hesabı seç</option>
                      {bankAccounts.map(b => <option key={b.id} value={b.id}>{b.account_name} — {b.bank_name}</option>)}
                    </select>
                  </>
                ) : (
                  <>
                    <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>Kasa:</span>
                    <select value={actionForm.cash_register_id || ''} onChange={e => setActionForm({ ...actionForm, cash_register_id: e.target.value })}
                      style={{ padding: '6px 10px', flex: 1, minWidth: 240 }}>
                      <option value="">Kasa seç</option>
                      {cashRegisters.map(c => <option key={c.id} value={c.id}>{c.register_name}{c.currency_code ? ' (' + c.currency_code + ')' : ''}</option>)}
                    </select>
                  </>
                )}
                <Button size="sm" onClick={() => performAction('mark_paid')} disabled={(actionForm.paid_target || 'bank') === 'bank' ? !actionForm.bank_account_id : !actionForm.cash_register_id}>Uygula</Button>
                <Button variant="ghost" size="sm" onClick={() => setActionMode(null)}>İptal</Button>
              </div>
            </div>
          )}
          {actionMode === 'endorse' && (
            <div style={{ marginBottom: 16, padding: '12px 14px', background: 'var(--bg-secondary)', borderRadius: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>Ciro Edilecek Cari:</span>
                <div style={{ flex: 1 }}>
                  <SearchableSelect
                    value={actionForm.target_account_id || ''}
                    onChange={id => setActionForm({ ...actionForm, target_account_id: id })}
                    options={accounts}
                    getLabel={cariLabel}
                    getSubLabel={cariSub}
                    searchKeys={['code', 'title', 'phone', 'tax_number', 'id_number']}
                  />
                </div>
                <Button size="sm" onClick={() => performAction('endorse')} disabled={!actionForm.target_account_id}>Uygula</Button>
                <Button variant="ghost" size="sm" onClick={() => setActionMode(null)}>İptal</Button>
              </div>
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
            <FormField label={`${docLabel} Numarası`}>
              <input value={form.doc_number || ''} onChange={e => f('doc_number', e.target.value)} disabled={!isEditing} />
            </FormField>
            <FormField label="Seri No">
              <input value={form.serial_no || ''} onChange={e => f('serial_no', e.target.value)} disabled={!isEditing} />
            </FormField>
            <FormField label="İlgili Cari" fullWidth>
              {isEditing ? (
                <SearchableSelect
                  value={form.account_id || ''}
                  onChange={id => f('account_id', id)}
                  options={accounts}
                  getLabel={cariLabel}
                  getSubLabel={cariSub}
                  searchKeys={['code', 'title', 'phone', 'tax_number', 'id_number']}
                  placeholder="Cari seçin"
                />
              ) : (
                <input disabled value={selected?.account_title ? formatCariOption({ code: selected.account_code, title: selected.account_title }) : '—'} />
              )}
            </FormField>
            <FormField label="Keşideci Adı">
              <input value={form.issuer_name || ''} onChange={e => f('issuer_name', e.target.value)} disabled={!isEditing} />
            </FormField>
            <FormField label="Keşideci Vergi/TC No">
              <input value={form.issuer_tax_no || ''} onChange={e => f('issuer_tax_no', e.target.value)} disabled={!isEditing} />
            </FormField>
            {docType === 'check' && (
              <>
                <FormField label="Banka">
                  <input value={form.bank_name || ''} onChange={e => f('bank_name', e.target.value)} disabled={!isEditing} />
                </FormField>
                <FormField label="Şube">
                  <input value={form.branch_name || ''} onChange={e => f('branch_name', e.target.value)} disabled={!isEditing} />
                </FormField>
                <FormField label="Hesap No">
                  <input value={form.account_number || ''} onChange={e => f('account_number', e.target.value)} disabled={!isEditing} />
                </FormField>
              </>
            )}
            <FormField label="Keşide Tarihi">
              <input type="date" value={form.issue_date || ''} onChange={e => f('issue_date', e.target.value)} disabled={!isEditing} />
            </FormField>
            <FormField label="Vade Tarihi" required>
              <input type="date" value={form.due_date || ''} onChange={e => f('due_date', e.target.value)} disabled={!isEditing} />
            </FormField>
            <FormField label="Tutar" required>
              <input type="number" step="0.01" value={form.amount || ''} onChange={e => f('amount', e.target.value)} disabled={!isEditing} />
            </FormField>
            <FormField label="Para Birimi">
              <select value={form.currency_code || 'TRY'} onChange={e => f('currency_code', e.target.value)} disabled={!isEditing}>
                <option>TRY</option><option>USD</option><option>EUR</option><option>GBP</option>
              </select>
            </FormField>
            <FormField label="Notlar" fullWidth>
              <textarea rows="3" value={form.notes || ''} onChange={e => f('notes', e.target.value)} disabled={!isEditing} />
            </FormField>
          </div>

          {!isNew && selected && (
            <div style={{ marginTop: 14, padding: 10, background: 'var(--bg-secondary)', borderRadius: 6, fontSize: '0.8rem' }}>
              <strong>Durum Geçmişi:</strong>
              <div style={{ marginTop: 6, display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '4px 12px' }}>
                {selected.given_to_bank_date && <><span>Bankaya verildi:</span><span>{new Date(selected.given_to_bank_date).toLocaleDateString('tr-TR')} ({selected.given_bank_name || '-'})</span></>}
                {selected.endorsed_date && <><span>Ciro edildi:</span><span>{new Date(selected.endorsed_date).toLocaleDateString('tr-TR')} ({selected.endorsed_to_title || '-'})</span></>}
                {selected.paid_date && <>
                  <span>{selected.direction === 'outgoing' ? 'Ödendi:' : 'Tahsil Edildi:'}</span>
                  <span>
                    {new Date(selected.paid_date).toLocaleDateString('tr-TR')}
                    {selected.paid_to_bank_name && ` · 🏦 ${selected.paid_to_bank_name}`}
                    {selected.paid_to_cash_name && ` · 💵 ${selected.paid_to_cash_name}`}
                  </span>
                </>}
                {selected.dishonored_date && <><span>Karşılıksız:</span><span>{new Date(selected.dishonored_date).toLocaleDateString('tr-TR')}</span></>}
                {selected.cancelled_date && <><span>İptal:</span><span>{new Date(selected.cancelled_date).toLocaleDateString('tr-TR')}</span></>}
              </div>
            </div>
          )}

          {/* ÖZEL ALANLAR — modal sonunda */}
          {!isNew && selected?.id && (
            <div style={{ marginTop: 18, paddingTop: 14, borderTop: '1px solid var(--border-secondary)' }}>
              <CustomFieldsSection entityType="check_note" entityId={selected.id} autoSave disabled={!inEdit} />
            </div>
          )}
        </Modal>
      )}
    </div>
  );
}

export default ChecksNotesPage;
